'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ImageField } from '@prismicio/types';
import { useAnimationFrame } from 'framer-motion';
import { LabelsDocument } from '@/prismicio-types';

interface Logo {
  logo: ImageField;
}

interface LogoMarqueeProps {
  logos: Logo[];
  labels: LabelsDocument;
}

export function LogoMarqueeInfinite({ logos }: LogoMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(0);
  const [loopWidth, setLoopWidth] = useState(0);
  const [logoElements, setLogoElements] = useState<Logo[]>([]);
  const speed = 0.5; // Pixels per frame - adjust for speed

  // Set up logos with duplicates for seamless loop
  useEffect(() => {
    // Use only logos, no text columns
    setLogoElements([...logos, ...logos, ...logos]); // Triple the logos for seamless looping
  }, [logos]);

  // Calculate widths on mount and resize
  useEffect(() => {
    const calculateWidths = () => {
      if (containerRef.current) {
        // Approximate the width of a single logo item
        const singleLogoWidth = 180; // Base width from sm:w-[180px]
        const totalLogosWidth = logos.length * singleLogoWidth;
        setLoopWidth(totalLogosWidth);
      }
    };

    calculateWidths();
    window.addEventListener('resize', calculateWidths);
    return () => window.removeEventListener('resize', calculateWidths);
  }, [logos.length]);

  // Animation frame for smooth scrolling
  useAnimationFrame(() => {
    if (loopWidth > 0) {
      let newPosition = position - speed;

      // If we've scrolled past one set of logos, reset position to create illusion of infinite loop
      if (newPosition <= -loopWidth) {
        newPosition = 0;
      }

      setPosition(newPosition);
    }
  });

  const leftPadding = 160;

  return (
    <a href="#reviews" className="block cursor-pointer" aria-label="View customer reviews">
      <section className="relative h-[104px] w-full overflow-hidden bg-gray-100 sm:h-[140px]">
        <div className="relative mx-auto flex h-full max-w-6xl overflow-hidden" ref={containerRef}>
          <div className="absolute left-0 top-0 z-10 h-full w-1/6 bg-gradient-to-r from-gray-100 to-transparent" />
          <div className="absolute right-0 top-0 z-10 h-full w-1/6 bg-gradient-to-l from-gray-100 to-transparent" />

          <div
            className="absolute flex h-full items-center"
            style={{
              paddingLeft: `${leftPadding}px`,
              paddingRight: '100px',
              transform: `translateX(${position}px)`,
              willChange: 'transform',
            }}
          >
            {logoElements.map((item, index) => (
              <div
                key={index}
                className="relative flex h-full w-40 flex-shrink-0 items-center justify-center px-2 sm:w-[180px] sm:px-4 md:px-5"
              >
                <span className="bg-gradient absolute left-0 block h-[80px] w-[1px] bg-gradient-to-t from-transparent via-black to-transparent opacity-10">
                  &nbsp;
                </span>
                {item.logo.url ? (
                  <Image
                    src={item.logo.url || '/placeholder.svg'}
                    alt={item.logo.alt || `Logo ${index + 1}`}
                    width={item.logo.dimensions?.width || 220}
                    height={item.logo.dimensions?.height || 132}
                    sizes="(max-width: 640px) 160px, (max-width: 768px) 180px, (max-width: 1024px) 200px, 220px"
                    className="pointer-events-none max-h-[120px] w-full object-contain px-2 opacity-50 sm:px-1"
                    quality={75}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -bottom-[10px] z-10 h-[10px] w-full shadow-[0px_-5px_5px_rgba(0,0,0,0.05)]"></div>
      </section>
    </a>
  );
}
